
import Vue, { PropType } from 'vue';
import { CvRow, CvColumn, CvButton, CvInlineNotification } from '@carbon/vue';
import { SmallDetailsResult, SmallDetailsPayload } from '@/typings';
import Close16 from '@carbon/icons-vue/es/close/16';
import InformationFilled16 from '@carbon/icons-vue/es/information--filled/16';
import { base64StringToBlob } from 'blob-util';
import { useStore as useShapeStoreV2 } from '@/stores/v2/shape';
import {
  magnify,
  removeMagnify,
} from '@/components/SmallDetails/utils/magnify';
import ZoomArea24 from '@carbon/icons-vue/es/zoom--area/24';
import AddAlt24 from '@carbon/icons-vue/es/add--alt/24';
import SubtractAlt24 from '@carbon/icons-vue/es/subtract--alt/24';
import { SmallDetailsStatus } from '@midocean/webapps-shared-domain';
import { getSmallDetailsStatus } from '@/utils/smallDetailsHelperV2';
import SmallDetailsOptions from '../SmallDetailsOptions/SmallDetailsOptions.vue';
import {
  CONTINUE_WITH_CURRENT_LOGO,
  REQUEST_LOGO_IMPROVEMENTS,
  SELECT_REVISED_LOGO,
} from '@/constants/smallDetails';
import { useStore as useProductStoreV2 } from '@/stores/v2/product';
import { WizardPayload } from '@/web-components/logo-library/typings';
import { fileTypeValidatorSmallDetails } from '../../../validators/product/fileTypeValidatorSmallDetails';
import { mapState } from 'pinia';
import {
  LogoShape,
  AnnotationShape,
} from '@/web-components/import-wizard-v2/typings/common';

export default Vue.extend({
  name: 'SmallDetailsOneLogo',
  components: {
    CvColumn,
    CvRow,
    Close16,
    InformationFilled16,
    CvButton,
    ZoomArea24,
    AddAlt24,
    SubtractAlt24,
    CvInlineNotification,
    SmallDetailsOptions,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
      default: false,
    },
    smallDetailsData: {
      type: Object as PropType<SmallDetailsResult>,
      required: true,
    },
    pngFile: {
      type: Blob,
      default: undefined,
      required: false,
    },
    fileName: {
      type: String,
      default: '',
      required: false,
    },
    wizardPayload: {
      type: Object as PropType<WizardPayload>,
      required: false,
    },
  },
  data() {
    return {
      activeIdx: 1,
      isZoomEnabled: false,
      zoomSize: 2,
      selectedOption: '',
      logoImprovementFormData: {
        improvementOption: '',
        improvementOptionKey: '',
        comment: '',
      },
      showImpovementRequestSuccess: false,
    };
  },

  computed: {
    ...mapState(useProductStoreV2, ['data']),
    status(): SmallDetailsStatus {
      return getSmallDetailsStatus(
        this.smallDetailsData.positiveDetailsLoss,
        this.smallDetailsData.negativeDetailsLoss,
        this.smallDetailsData.calculatedQuality
      );
    },
    currentShape(): AnnotationShape {
      return this.relevantShapes[this.activeIdx - 1];
    },
    relevantShapes(): AnnotationShape[] {
      const store = useShapeStoreV2();

      return store.shapes.filter((x) => x.smallDetailsResult !== undefined);
    },
    uploadButtonLabel(): string {
      if (this.status === 'Bad') {
        return this.$t('section.actions.upload-better-logo').toString();
      }
      if (this.status === 'Average') {
        return this.$t('section.actions.upload-a-good-logo').toString();
      }

      if (this.status === 'NoCheck') {
        return this.$t('section.actions.proceed-without-check').toString();
      }
      return this.$t('section.actions.import').toString();
    },
    proceedButtonLabel(): string {
      if (this.status === 'Bad') {
        return this.$t('section.actions.proceed-with-this-bad-logo').toString();
      }
      if (this.status === 'Average') {
        return this.$t('section.actions.proceed-with-this-logo').toString();
      }
      if (this.status === 'NoCheck') {
        return this.$t('section.actions.upload-new-logo').toString();
      }
      return this.$t('section.actions.back-to-library').toString();
    },
    imageUrl(): string | undefined {
      if (this.data) {
        // check if the pngFile is a base64 string or a url
        const isSmallDetailsPngFileUrlFormat = this.smallDetailsData.pngFile
          ? this.smallDetailsData.pngFile.startsWith('http')
          : false;
        const smallDetailsPngFile = isSmallDetailsPngFileUrlFormat
          ? this.smallDetailsData.pngFile
          : URL.createObjectURL(
              base64StringToBlob(this.smallDetailsData.pngFile, 'image/png')
            );
        return this.status !== 'NoCheck'
          ? this.data && this.smallDetailsData.pngFile
            ? smallDetailsPngFile
            : ''
          : URL.createObjectURL(this.pngFile as Blob);
      } else {
        if (this.currentShape) {
          if (this.currentShape.kind === 'Text') {
            return this.currentShape.smallDetailsResult &&
              this.currentShape.smallDetailsResult.status !== 'NoCheck'
              ? URL.createObjectURL(
                  this.currentShape.smallDetailsResult.pngFileBlob
                )
              : undefined;
          }

          if (this.currentShape.kind === 'Logo') {
            return this.currentShape.smallDetailsResult &&
              this.currentShape.smallDetailsResult.status !== 'NoCheck'
              ? URL.createObjectURL(
                  this.currentShape.smallDetailsResult.pngFileBlob
                )
              : URL.createObjectURL(this.currentShape.pngFile as Blob);
          }
        }
        return undefined;
      }
    },
    smallDetailsPayload(): SmallDetailsPayload {
      return {
        negativeDetailsLoss: this.smallDetailsData.negativeDetailsLoss,
        positiveDetailsLoss: this.smallDetailsData.positiveDetailsLoss,
        pngFileBlob:
          this.smallDetailsData.pngFile &&
          this.smallDetailsData.pngFile.slice(0, 5) !== 'https'
            ? base64StringToBlob(this.smallDetailsData.pngFile, 'image/png')
            : new Blob(),
        status: this.status,
        calculatedQuality: this.smallDetailsData.calculatedQuality,
        pngFile: this.smallDetailsData.pngFile
          ? this.smallDetailsData.pngFile
          : '',
        isCheckValid: true,
      } as SmallDetailsPayload;
    },
    isContinueButtonDisabled(): boolean {
      if (
        this.selectedOption === REQUEST_LOGO_IMPROVEMENTS &&
        !Object.values(this.logoImprovementFormData.improvementOption)[0]
      ) {
        return true;
      } else {
        return (
          this.status !== 'Good' &&
          this.status !== 'NoCheck' &&
          this.selectedOption === ''
        );
      }
    },
  },

  watch: {
    isOpen(newValue) {
      if (newValue) {
        this.$modal.show('small-details-single-logo-modal');
      } else {
        this.$modal.hide('small-details-single-logo-modal');
      }
    },
    activeIdx() {
      // reset magnify glass
      const subtarget = document.querySelector('w2p-import-wizard-wrapper');
      let element = undefined;
      let tagName = 'w2p-import-wizard-wrapper';

      if (subtarget && subtarget.shadowRoot) {
        element = subtarget.shadowRoot.getElementById('magnifier-glass');
      } else {
        const oldVersionTarget = document.querySelector('w2p-import-wizard');
        tagName = 'w2p-import-wizard';
        element =
          oldVersionTarget && oldVersionTarget.shadowRoot
            ? oldVersionTarget.shadowRoot.getElementById('magnifier-glass')
            : document.getElementById('magnifier-glass');
      }

      // TODO: in 2.18 version revert to old changes
      // const element =
      //   subtarget && subtarget.shadowRoot
      //     ? subtarget.shadowRoot.getElementById('magnifier-glass')
      //     : document.getElementById('magnifier-glass');

      element && element.remove();
      magnify('myimage', this.zoomSize, tagName);
    },
    zoomSize() {
      const subtarget = document.querySelector('w2p-import-wizard-wrapper');
      let tagName = 'w2p-import-wizard-wrapper';

      if (subtarget === undefined) {
        tagName = 'w2p-import-wizard';
      }

      removeMagnify(tagName);
      magnify('myimage', this.zoomSize, tagName);
    },
  },

  mounted() {
    if (this.isOpen) {
      this.$modal.show('small-details-single-logo-modal');
    }

    // this.runFileTypeValidator();
  },

  methods: {
    updateLogoImprovementForm(form: any) {
      this.logoImprovementFormData = { ...form };
    },
    onCancel() {
      this.$modal.hide('small-details-single-logo-modal');
    },
    onUpload() {
      if (this.showImpovementRequestSuccess === true) {
        this.$modal.hide('small-details-single-logo-modal');
      }

      if (
        this.selectedOption === CONTINUE_WITH_CURRENT_LOGO ||
        this.status === 'Good' ||
        this.status === 'NoCheck'
      ) {
        this.$emit('action:proceed-with-logo', this.smallDetailsPayload);
      }

      if (this.selectedOption === SELECT_REVISED_LOGO) {
        this.$emit('action:upload-new-logo');
      }

      if (this.selectedOption === REQUEST_LOGO_IMPROVEMENTS) {
        this.showImpovementRequestSuccess = true;
        this.$emit('action:request-improvement', this.logoImprovementFormData);
      }
    },
    enableZoom() {
      const subtarget = document.querySelector('w2p-import-wizard-wrapper');
      let element = undefined;
      let tagName = 'w2p-import-wizard-wrapper';

      if (subtarget && subtarget.shadowRoot) {
        element = subtarget.shadowRoot.getElementById('magnifier-glass');
      } else {
        const oldVersionTarget = document.querySelector('w2p-import-wizard');
        if (oldVersionTarget && oldVersionTarget.shadowRoot) {
          tagName = 'w2p-import-wizard';
        }
        element =
          oldVersionTarget && oldVersionTarget.shadowRoot
            ? oldVersionTarget.shadowRoot.getElementById('magnifier-glass')
            : document.getElementById('magnifier-glass');
      }

      // TODO: in 2.18 version revert to old changes
      // const element =
      //   subtarget && subtarget.shadowRoot
      //     ? subtarget.shadowRoot.getElementById('magnifier-glass')
      //     : document.getElementById('magnifier-glass');

      if (element) {
        this.isZoomEnabled = false;
        this.zoomSize = 2;
        removeMagnify(tagName);
      } else {
        this.isZoomEnabled = true;
        magnify('myimage', this.zoomSize, tagName);
      }
    },
  },
});
